import { Edit2, Trash2, Mail, Phone, Clock } from 'lucide-react';
import { format } from 'date-fns';
import type { Lead } from '../../types/lead';
import { Button, EmptyState, LoadingSpinner, Badge, Pagination } from '../ui';

interface LeadListProps {
  leads: Lead[];
  onEdit: (lead: Lead) => void;
  onDelete: (lead: Lead) => void;
  onUpdateContact: (lead: Lead) => void;
  isLoading: boolean;
  pagination?: {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
  };
  onPageChange?: (page: number) => void;
}

function LeadList({
  leads,
  onEdit,
  onDelete,
  onUpdateContact,
  isLoading,
  pagination,
  onPageChange,
}: LeadListProps) {
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!leads || leads.length === 0) {
    return (
      <EmptyState
        title="Nenhum lead encontrado"
        description="Crie seu primeiro lead para começar."
      />
    );
  }

  return (
    <>
      <div className="mt-8 bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Nome
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Contato
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Vendedor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Origem
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Último Contato
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {leads.map(lead => (
                <tr key={lead.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {lead.full_name}
                    </div>
                    {lead.lead_group_ids && lead.lead_group_ids.length > 0 && (
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {lead.lead_group_ids.length} grupo(s)
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      {lead.email && (
                        <div className="flex items-center text-sm text-gray-900 dark:text-white">
                          <Mail className="h-3 w-3 mr-1" />
                          {lead.email}
                        </div>
                      )}
                      {lead.phone && (
                        <div className="flex items-center text-sm text-gray-900 dark:text-white">
                          <Phone className="h-3 w-3 mr-1" />
                          {lead.phone}
                        </div>
                      )}
                      {lead.phone2 && (
                        <div className="flex items-center text-sm text-gray-900 dark:text-white">
                          <Phone className="h-3 w-3 mr-1" />
                          {lead.phone2}
                        </div>
                      )}
                      {lead.phone3 && (
                        <div className="flex items-center text-sm text-gray-900 dark:text-white">
                          <Phone className="h-3 w-3 mr-1" />
                          {lead.phone3}
                        </div>
                      )}
                      {!lead.email && !lead.phone && !lead.phone2 && !lead.phone3 && (
                        <span className="text-sm text-gray-500 dark:text-gray-400">-</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {lead.representative_id ? (
                        <Badge variant="info">Atribuído</Badge>
                      ) : (
                        <Badge variant="warning">Não atribuído</Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {lead.origin || '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge variant="secondary">
                      {lead.type === 'administrative' && 'Administrativo'}
                      {lead.type === 'registration' && 'Cadastro'}
                      {lead.type === 'commercial' && 'Comercial'}
                      {lead.type === 'financial' && 'Financeiro'}
                      {lead.type === 'legal_representative' && 'Rep. Legal'}
                      {lead.type === 'technical' && 'Técnico'}
                      {lead.type === 'other' && 'Outro'}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {lead.last_contact_at ? (
                      <div className="text-sm text-gray-900 dark:text-white">
                        {format(new Date(lead.last_contact_at), 'dd/MM/yyyy HH:mm')}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500 dark:text-gray-400">Nunca</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onUpdateContact(lead)}
                        title="Atualizar último contato"
                      >
                        <Clock className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onEdit(lead)}
                        title="Editar"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => onDelete(lead)}
                        title="Excluir"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {pagination && pagination.last_page > 1 && onPageChange && (
        <div className="mt-4">
          <Pagination
            currentPage={pagination.current_page}
            totalPages={pagination.last_page}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </>
  );
}

export default LeadList;
